// src/pages/Appli/AppliAdmin/NewsAdmin/EditNewsModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  updateNews,
  uploadFirstImageNews,
  uploadSecondImageNews,
} from "./../../../../services/newsServices";

import { useNotification } from "./../../../../components/Notification/NotificationManager";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const isExternalURL = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const toAbs = (url) => {
  if (!url) return "";
  return isExternalURL(url) ? url : `${BASE_URL}${url}`;
};

const normLang = (l) => {
  const v = String(l || "fr").toLowerCase();
  if (v.startsWith("fr")) return "fr";
  if (v.startsWith("en")) return "en";
  return "fr";
};

export default function EditNewsModal({ news, onClose, onUpdate, language }) {
  const { addNotification } = useNotification();
  const lang = useMemo(() => normLang(language), [language]);

  const titleKey = `title_${lang}`;
  const contentKey = `content_${lang}`;

  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // refs pour reset input file (permet re-upload du même fichier)
  const fileRef1 = useRef(null);
  const fileRef2 = useRef(null);

  useEffect(() => {
    if (!news?.id) return;

    setFormData({
      id: news.id,
      title_fr: news.title_fr || "",
      title_en: news.title_en || "",
      content_fr: news.content_fr || "",
      content_en: news.content_en || "",
      image_url: news.image_url || "",
      image_url2: news.image_url2 || "",
      ordre: news.ordre ?? "",
      is_active: news.is_active ?? true,
    });
  }, [news]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, [contentKey]: value }));
  };

  const handleImageUpload = async (event, which) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("image", file);

    setIsLoading(true);
    try {
      const uploadResponse =
        which === "image1"
          ? await uploadFirstImageNews(fd)
          : await uploadSecondImageNews(fd);

      if (!uploadResponse?.filePath) throw new Error("Réponse upload invalide");

      setFormData((prev) => ({
        ...prev,
        ...(which === "image1"
          ? { image_url: uploadResponse.filePath }
          : { image_url2: uploadResponse.filePath }),
      }));

      addNotification(
        which === "image1"
          ? "Image principale mise à jour ✅"
          : "Deuxième image mise à jour ✅",
        "success"
      );
    } catch (error) {
      console.error("Erreur upload image :", error);
      addNotification("Erreur upload image.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Suppression côté UI : on vide le champ + reset l'input file
  const removeImage = (which) => {
    setFormData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        ...(which === "image1" ? { image_url: "" } : { image_url2: "" }),
      };
    });

    if (which === "image1" && fileRef1.current) fileRef1.current.value = "";
    if (which === "image2" && fileRef2.current) fileRef2.current.value = "";

    addNotification(
      which === "image1" ? "Image principale supprimée (à enregistrer) 🗑️" : "Deuxième image supprimée (à enregistrer) 🗑️",
      "success"
    );
  };

  const handleSubmit = async () => {
    if (!formData?.id) {
      addNotification("Erreur : ID manquant.", "error");
      return;
    }

    try {
      const payload = {
        ...formData,
        ordre: formData.ordre !== "" ? Number(formData.ordre) : 0,
        is_active: !!formData.is_active,
        // image_url / image_url2 peuvent être "" => ton back doit l'accepter
      };

      await updateNews(formData.id, payload);

      addNotification("Actualité mise à jour avec succès ✅", "success");
      onUpdate?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur update actu :", error);
      addNotification("Erreur lors de la mise à jour.", "error");
    }
  };

  if (!formData) return null;

  const img1 = toAbs(formData.image_url);
  const img2 = toAbs(formData.image_url2);

  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-black/10 dark:border-white/10">
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">
            Modifier l'actualité
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Langue : <span className="font-bold">{lang.toUpperCase()}</span>
          </p>
        </div>

        <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Titre */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Titre ({lang.toUpperCase()})
            </label>
            <input
              type="text"
              name={titleKey}
              value={formData[titleKey] || ""}
              onChange={handleChange}
              className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/20 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Contenu */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Contenu ({lang.toUpperCase()})
            </label>
            <div className="rounded-3xl border border-black/10 dark:border-white/10 overflow-hidden bg-white dark:bg-black/20">
              <ReactQuill
                theme="snow"
                value={formData[contentKey] || ""}
                onChange={handleContentChange}
              />
            </div>
          </div>

          {/* Images + suppression */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Image 1 */}
            <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 p-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Image principale
              </label>

              {img1 ? (
                <img
                  src={img1}
                  alt="Image 1"
                  className="w-full rounded-2xl object-cover max-h-48 mb-3"
                />
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-300 mb-3">
                  Aucune image
                </div>
              )}

              <input
                ref={fileRef1}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "image1")}
                disabled={isLoading}
              />

              <div className="mt-3 flex justify-between gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-300 break-all">
                  {formData.image_url || ""}
                </span>

                <button
                  type="button"
                  onClick={() => removeImage("image1")}
                  disabled={isLoading || !formData.image_url}
                  className="shrink-0 rounded-xl px-3 py-1.5 text-xs font-extrabold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                >
                  Supprimer
                </button>
              </div>
            </div>

            {/* Image 2 */}
            <div className="rounded-3xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 p-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Deuxième image
              </label>

              {img2 ? (
                <img
                  src={img2}
                  alt="Image 2"
                  className="w-full rounded-2xl object-cover max-h-48 mb-3"
                />
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-300 mb-3">
                  Aucune image
                </div>
              )}

              <input
                ref={fileRef2}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, "image2")}
                disabled={isLoading}
              />

              <div className="mt-3 flex justify-between gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-300 break-all">
                  {formData.image_url2 || ""}
                </span>

                <button
                  type="button"
                  onClick={() => removeImage("image2")}
                  disabled={isLoading || !formData.image_url2}
                  className="shrink-0 rounded-xl px-3 py-1.5 text-xs font-extrabold bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>

          {/* Ordre + actif */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Ordre
              </label>
              <input
                type="number"
                name="ordre"
                value={formData.ordre}
                onChange={handleChange}
                className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-black/20 px-3 py-2 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex items-end">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  className="h-5 w-5 accent-primary"
                />
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Actif
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-bold border border-black/10 dark:border-white/10 bg-white/80 dark:bg-black/20 text-gray-900 dark:text-white hover:bg-white dark:hover:bg-black/30 transition"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="rounded-2xl px-4 py-2 text-sm font-extrabold bg-primary text-white hover:opacity-95 transition disabled:opacity-60"
          >
            {isLoading ? "Chargement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
